import { Injectable } from '@angular/core';
import { Observable, of, from, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ActivityTrackerService } from './activity-tracker.service';
import { ConfigService } from './config.service';
import {
  HandlerOptions,
  MessageHandlerService,
  MessageItem,
  MessageTypes,
} from './message-handler.service';
import { UserPermissionsService } from './user-permissions.service';
import { ILog } from '../models/log.model';
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { RightsEnum } from '../enums/rights.enum';
import { IHttpParams } from '../home/filter/filter.model';

const HTTP_STATUS_CODES = {
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  RequestTimeout: 408,
  InternalServerError: 500,
  ServiceUnavailable: 503,
};

const HTTP_HEADERS = {
  Authorization: 'Authorization',
};

export interface HttpRequestOptions extends HandlerOptions { }

export interface HttpResponseError {
  ErrorCode: string;
  FieldName: string;
  Message: string;
}
export interface HttpResponseStatus {
  ErrorCode: string;
  Message: string;
  Errors: HttpResponseError[];
}

@Injectable()
export class ApiHttpService {
  private _defaultOptions: HttpRequestOptions = {};
  private _urlPrefix: string;

  constructor(
    private _http: HttpClient,
    private _msgService: MessageHandlerService,
    private _activityTracker: ActivityTrackerService,
    private _configService: ConfigService,
    private _auth: UserPermissionsService,
    private _storage: LocalStorageService,
    private _router: Router
  ) {
    //this._urlPrefix = this._configService.config?.apiBaseURL;
    this._urlPrefix = `${environment.apiURL}${environment.apiBaseURL}`;
  }

  public get(
    route: string,
    reqOptions?: HttpRequestOptions,
    params?: IHttpParams,
    responseType: 'json' | 'blob' = 'json'
  ): Observable<any> {

    let allOptions: HttpRequestOptions = this.getRequestOptionsWithDefaults(reqOptions);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    let url = this.buildUrl(route);

    this._activityTracker.activityStart();

    if (responseType === 'blob') {
      return this._http.get(url, {
        headers: headers,
        observe: 'response',
        responseType: 'blob',
        params
      }).pipe(
        tap((res: any) => this.processResponse(res, allOptions, '')),
        catchError(err => this.processError(err, allOptions)),
        finalize(() => this._activityTracker.activityStop())
      ) as Observable<HttpResponse<Blob>>;
    }

    return this._http.get(url, { headers: headers, observe: 'response', params }).pipe(
      tap((res: any) => this.processResponse(res, allOptions, '')),
      map(res => res.body),
      catchError(err => this.processError(err, allOptions)),
      finalize(() => this._activityTracker.activityStop())
    );
  }

  public post<T>(
    route: string,
    body: any,
    reqOptions?: HttpRequestOptions,
    hideSaved?: boolean,
    successMessage: string = 'Saved Successfully',
    responseType: 'json' | 'blob' = 'json',
    params?: IHttpParams
  ): Observable<any> {
    let allOptions: HttpRequestOptions =
      this.getRequestOptionsWithDefaults(reqOptions);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    let url = this.buildUrl(route);

    this._activityTracker.activityStart();
    if (responseType === 'blob') {
      return this._http.post(url, body, { headers: headers, observe: 'response', responseType: 'blob' as 'blob', params }).pipe(
        tap((res: any) => { this.processResponse(res, allOptions, hideSaved ? '' : successMessage); }),
        catchError(err => this.processError(err, allOptions)),
        finalize(() => this._activityTracker.activityStop())
      ) as Observable<HttpResponse<Blob>>;
    } else {
      return this._http.post(url, body, { headers: headers, observe: 'response', params }).pipe(
        tap(
          (res: any) => {
            this.processResponse(res, allOptions, hideSaved ? '' : successMessage)
          }
        ),
        map(res => res.body),
        catchError(err => this.processError(err, allOptions)),
        finalize(() => this._activityTracker.activityStop())
      );
    }
  }

  public put(
    route: string,
    body: any,
    reqOptions?: HttpRequestOptions
  ): Observable<Response> {
    let allOptions: HttpRequestOptions =
      this.getRequestOptionsWithDefaults(reqOptions);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    let url = this.buildUrl(route);
    let bodyText = JSON.stringify(body) || '';

    this._activityTracker.activityStart();

    return this._http.put(url, body, { headers: headers, observe: 'response' }).pipe(
      tap(
        (res: any) => { this.processResponse(res, allOptions, 'Saved Successfully') }
      ),
      map(res => res.body),
      catchError(err => this.processError(err, allOptions)),
      finalize(() => this._activityTracker.activityStop())
    );
  }

  public delete(
    route: string,
    reqOptions?: HttpRequestOptions
  ): Observable<Response> {
    let allOptions: HttpRequestOptions =
      this.getRequestOptionsWithDefaults(reqOptions);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    let url = this.buildUrl(route);

    this._activityTracker.activityStart();

    // return this._http
    //   .delete(url, { headers: headers })
    //   .pipe(
    //     map(
    //       (res: any) =>
    //         this.processResponse(res, allOptions, 'Deleted Successfully.'),
    //       (error: any) => this.processError(error, allOptions),
    //     )
    //   )
    //   .lift(() => this._activityTracker.activityStop());
    return this._http.delete(url, { headers: headers, observe: 'response' }).pipe(
      tap(
        (res: any) => { this.processResponse(res, allOptions, 'Deleted Successfully') }
      ),
      map(res => res.body),
      catchError(err => this.processError(err, allOptions)),
      finalize(() => this._activityTracker.activityStop())
    );
  }

  setDefaultOptions(options: HttpRequestOptions, replaceAll = false) {
    if (replaceAll) {
      this._defaultOptions = options;
    } else {
      Object.assign(this._defaultOptions, options);
    }
  }

  private getRequestOptionsWithDefaults(
    options?: HttpRequestOptions
  ): HttpRequestOptions {
    let allOptions = <HttpRequestOptions>{};
    Object.assign(allOptions, this._defaultOptions, options);
    return allOptions;
  }

  private buildUrl(route: string): string {
    // Normalize input
    const trimmedRoute = (route || '').trim();

    // Block absolute/external URLs (core fix for Veracode)
    const isAbsoluteUrl = /^(https?:)?\/\//i.test(trimmedRoute);

    if (isAbsoluteUrl) {
      console.error('Blocked unsafe route:', route);
      throw new Error('Invalid API route');
    }

    // Ensure only relative paths are used
    const safeRoute = trimmedRoute.replace(/^\/+/, '');

    let baseUrl =
      safeRoute.toLowerCase() === 'log'
        ? this._configService.config.logApiBaseURL
        : this._urlPrefix;

    // Ensure proper slash handling
    if (!baseUrl.endsWith('/')) {
      baseUrl += '/';
    }

    return baseUrl + safeRoute;
  }

  private processResponse(
    response: any,
    reqOptions: HttpRequestOptions,
    successMessage: string
  ) {
    let msgType: string = this.getMsgType(response?.status);
    let respStatus: HttpResponseStatus;
    let messages: MessageItem[] = [];
    let userPermissions = JSON.parse(
      response.headers.get('AUTH_RIGHTS') || '{}'
    );
    let userInfo = JSON.parse(
      response.headers.get('USER_INFO') || '{}'
    );

    if (!response.ok) {
      response.error.validationErrors.forEach((val: { message: any; }) => {
        let errorMessage = val.message;
        messages.push({
          Type: MessageTypes.Danger,
          Message: errorMessage
        });
      })
    }

    if (messages.length === 0 && successMessage != '') {
      messages.push({ Type: MessageTypes.Success, Message: successMessage });
    }

    if (messages.length > 0) {
      this._msgService.setMessages(messages, reqOptions);
    }

    if (userPermissions != null && userPermissions.length != 0) {
      this._auth.setUserPermissions(userPermissions);
    }

    this._auth.setUserInfo(userInfo);


    return messages.length;
  }

  private processError(
    errResponse: HttpResponse<any>,
    reqOptions: HttpRequestOptions
  ) {
    //Server went offline
    if (errResponse.status === 0) {
      this._router.navigate(['/error/500']);
      return throwError(() => err);
    }

    if (errResponse.status === 500) {
      this._router.navigate(['/error/500']);
      return throwError(() => err);
    }

    let userPermissions = JSON.parse(
      errResponse.headers.get('AUTH_RIGHTS') || '{}'
    );

    if (userPermissions != null && userPermissions.length != 0) {
      this._auth.setUserPermissions(userPermissions);
    }

    if (errResponse.status === 401) {
      this._router.navigate(['/error/401']);
      return throwError(() => err);
    }

    let msgCount = this.processResponse(errResponse, reqOptions, '');
    if (msgCount === 0) {
      let msgType: string = this.getMsgType(errResponse.status);
      this._msgService.setMessage(
        msgType,
        `Server Error: ${errResponse.status}`,
        undefined,
        undefined,
        reqOptions
      );
    }

    let err: any;
    try {
      err = errResponse.body;
    } catch (e) {
      err = errResponse;
    }

    let log: ILog = {
      message: errResponse.url || '',
      detail: err?.error || err,
      machineName: 'Browser - ' + navigator.userAgent,
      component: 1,
      severity: 3,
      user: this._auth.getUser511(),
    };

    return throwError(() => err);
  }

  private getMsgType(sts: number) {
    if (sts >= 500 && sts <= 599) {
      return MessageTypes.Danger;
    }
    if (sts >= 400 && sts <= 499) {
      return MessageTypes.Danger;
    }
    if (sts >= 200 && sts <= 299) {
      return MessageTypes.Success;
    }
    return MessageTypes.Info;
  }


  // Method to download a CSV file from the specified route, with optional HTTP parameters and activity tracking
  public downloadCsvFile(
    route: string,
    params?: HttpParams
  ): Observable<string> {
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');
    let url = this.buildUrl(route);
    this._activityTracker.activityStart();
    return this._http.get(url, { headers: headers, params, responseType: 'text' }).pipe(
      finalize(() => this._activityTracker.activityStop())
    );

  }

}
